import React from 'react';
import PromptTagEditor from '../components/PromptTagEditor';
import PromptTagReferenceLabel from '../components/PromptTagReferenceLabel';

const ExtraAnalysisConfigurator = ({
  title,
  onSave,
  onDiscard,
  highlightTemplatePicker = false,
  isCollapsed,
  onToggleCollapsed,
  saveDisabled,
  discardDisabled,
  extraSetupSaveState,
  extraSetupData,
  loadingExtraSetup,
  extraSetupError,
  selectedExtraAnalysisType,
  onOpenTemplatePicker,
  templatePickerLoading,
  hasSelectedScript,
  selectedExtraPromptTagReferences,
  selectedExtraPromptTagWarning,
  extraPromptDraft,
  setExtraPromptDraft,
  selectedExtraPromptKnownTags,
  knownPromptTagPatterns,
}) => {
  const templatePickerDisabled = !hasSelectedScript || loadingExtraSetup || templatePickerLoading;
  const shouldAnimateTemplatePicker = highlightTemplatePicker && !templatePickerDisabled;

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <div className="flex items-center gap-2">
          {!isCollapsed && (
            <>
              <button
                type="button"
                onClick={onSave}
                disabled={saveDisabled}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  saveDisabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {extraSetupSaveState === 'saving' ? 'Saving…' : 'Save Changes'}
              </button>

              <button
                type="button"
                onClick={onDiscard}
                disabled={discardDisabled}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  discardDisabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-rose-600 text-white hover:bg-rose-700'
                }`}
              >
                Discard
              </button>
            </>
          )}

          <button
            type="button"
            onClick={onToggleCollapsed}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            {isCollapsed ? 'Open' : 'Close'}
          </button>
        </div>
      </div>

      {!isCollapsed && extraSetupData.missingPrompt && !loadingExtraSetup && (
        <div className="text-sm text-amber-600">
          No prompt saved yet. Start from a blank slate and save.
        </div>
      )}

      {!isCollapsed && extraSetupError && (
        <div className="text-sm text-red-600">{extraSetupError}</div>
      )}

      {!isCollapsed && extraSetupSaveState === 'saved' && (
        <div className="text-sm text-emerald-600">Saved successfully.</div>
      )}

      {!isCollapsed && (
        <div className="border border-gray-200 rounded-xl p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">
              {selectedExtraAnalysisType === 'sentiment-analysis' ? 'Sentiment Prompt' : 'Prompt Template'}
            </h3>
            <div className="flex items-center gap-2">
              {loadingExtraSetup && (
                <span className="text-xs text-gray-400">Loading…</span>
              )}
              <button
                type="button"
                onClick={onOpenTemplatePicker}
                disabled={templatePickerDisabled}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  templatePickerDisabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                } ${shouldAnimateTemplatePicker ? 'analytics-setup-template-picker-zoom' : ''}`}
              >
                Pick a template
              </button>
            </div>
          </div>
          <PromptTagReferenceLabel items={selectedExtraPromptTagReferences} />
          {selectedExtraPromptTagWarning && (
            <div className="text-xs text-amber-700">
              {selectedExtraPromptTagWarning}
            </div>
          )}
          <PromptTagEditor
            value={loadingExtraSetup ? 'Loading prompt…' : extraPromptDraft}
            onChange={setExtraPromptDraft}
            disabled={loadingExtraSetup || !hasSelectedScript}
            knownTags={selectedExtraPromptKnownTags}
            knownTagPatterns={knownPromptTagPatterns}
            minHeightClassName="min-h-[380px]"
          />
        </div>
      )}
    </section>
  );
};

export default ExtraAnalysisConfigurator;
